import { ObjectGroupControlFlags } from '../vehicle/object-group-control-flags-enum'

/**
 * Enumeration of all usable control flags.
 *
 * Add more if required.
 * @see {@link https://confluence.ttt-sp.com/x/-gru}
 *
 * @readonly
 * @enum {string}
 */
export const ControlFlags = {
    // UI feature set
    LITE: 'LITE',
    STANDARD: 'STANDARD',

    // UI features
    DRIVERS: 'drivers',
    DRIVERS_OPTIDRIVE: 'driversOptiDrive',
    DRIVERS_WORKING_TIMES: 'driversWorkingTimes',
    DRIVERS_DAILY_DRIVING_TIME: 'driversDailyDrivingTime',
    DRIVERS_MANUAL_VEHICLE_ASSIGNMENT: 'driversManualVehicleAssignment',
    DRIVERS_REMOTE_LINK_ASSIGNMENT: 'driversRemoteLinkAssignment',
    DRIVERS_TACHO_ASSIGNMENT: 'driversTachoAssignment',
    DRIVERS_ECO_DRIVER: 'driversEcoDriver',
    DRIVING_TIMES: 'drivingTimes',
    MESSAGES: 'messages',
    TABLE_CONFIGURABLE_COLUMNS: 'table.configurableColumns',
    DRIVER_TERMINALS_CONFIGURATION: 'driverTerminalsConfiguration',
    RPM_DATA: 'rpmData',
    GEAR_SHIFT_DATA: 'gearShiftData',
    VEHICLE_ACTIVITIES: 'vehicleActivities',
    SMART_CHARGING: 'smartCharging',
    SECURITY: 'security',
    SECURITY_VEHICLES: 'securityVehicles',

    // Driving Security Management Service rights
    VIEW_DRIVING_SECURITY: 'viewDrivingSecurity',
    EDIT_DRIVING_SECURITY: 'editDrivingSecurity',

    // User rights notifications
    ACKNOWLEDGE_EVENT: 'acknowledgeEvent',
    RESOLVE_EVENT: 'resolveEvent',
    NOTIFICATIONS: 'notifications',
    EDIT_NOTIFICATION_RULES: 'editNotificationRules',

    // User rights driver
    ACTIVATE_DRIVER: 'activateDriver',
    EDIT_DRIVER: 'editDriver',
    DELETE_DRIVER: 'deleteDriver',
    CHANGE_DRIVER_GROUP_MEMBERSHIP: 'changeDriverGroupMembership',
    DEACTIVATE_DRIVER: 'deactivateDriver',
    DELETE_DRIVER_GROUP: 'deleteDriverGroup',
    EDIT_DRIVER_GROUP: 'editDriverGroup',
    INSERT_DRIVER: 'insertDriver',
    INSERT_DRIVER_GROUP: 'insertDriverGroup',
    VIEW_DRIVER_GROUP: 'viewDriverGroup',
    RESET_PASSWORD: 'resetPassword',
    VIEW_DRIVER: 'viewDriver',

    // User rights objects
    VIEW_MESSAGE: 'viewMessage',
    VIEW_TRIP_RELATED_DATA: 'viewTripRelatedData',

    // User rights user
    USERS: 'users',
    DELETE_USER: 'deleteUser',
    EDIT_USER: 'editUser',
    INSERT_USER: 'insertUser',
    RIGHT_MANAGEMENT_STANDARD: 'usersRightManagementStandard',
    RIGHT_MANAGEMENT_ADVANCED: 'usersRightManagementAdvanced',
    USERS_ASSIGNMENT_INTERFACE: 'usersAssignmentInterface',
    VIEW_USER: 'viewUser',
    CHANGE_OWN_PASSWORD: 'changeOwnPassword',

    // Cameras rights
    CAMERAS: 'videos',
    VIDEO_FEATURES_ENABLED: 'videoFeaturesEnabled',
    VIEW_VIDEO_EVENTS: 'viewVideoEvents',
    INSERT_VIDEO_EVENTS: 'insertVideoEvents',
    DOWNLOAD_VIDEO_EVENTS: 'downloadVideoEvents',
    DELETE_VIDEO_EVENTS: 'deleteVideoEvents',
    VIEW_CAMERAS: 'viewCamera',
    EDIT_CAMERAS: 'editCamera',
    CAMERA_FEATURE_BASIC: 'wf_video_basic',
    CAMERA_FEATURE_PLUS: 'wf_video_plus',
    CAMERA_FEATURE_LIVE: 'wf_video_live',
    CAMERA_FEATURE_BASIC_INT: 'wf_video_basic_int',
    CAMERA_FEATURE_PLUS_INT: 'wf_video_plus_int',
    CAMERA_FEATURE_LIVE_INT: 'wf_video_live_int',

    // Address rights
    ADDRESSES: 'addresses',
    DELETE_ADDRESS: 'deleteAddress',
    EDIT_ADDRESS: 'editAddress',
    INSERT_ADDRESS: 'insertAddress',
    VIEW_ADDRESS: 'viewAddress',

    // Address group rights
    DELETE_ADDRESS_GROUP: 'deleteAddressGroup',
    EDIT_ADDRESS_GROUP: 'editAddressGroup',
    INSERT_ADDRESS_GROUP: 'insertAddressGroup',
    VIEW_ADDRESS_GROUP: 'viewAddressGroup',

    AREAS: 'areas',
    VIEW_AREA: 'viewArea',
    TEMPORARY_AREAS_CORRIDORS: 'temporary_areas_corridors',

    // Object rights
    // @TODO [WASAPI-252]: Rename to 'editObject' once WASAPI has been fixed and will deliver that control flag
    CHANGE_OBJECT_GROUP_MEMBERSHIP: 'changeObjectGroupMembership',
    EDIT_EVENT_FORWARDING_CONFIG: 'editEventForwardingConfig',
    EDIT_OBJECT: 'editVehicleData',
    EDIT_OBJECT_DEVICE_CONFIGURATION: 'configUnit',
    HOURS_OF_SERVICE: 'hours_of_service',
    INSERT_OBJECT_GROUP: 'insertObjectGroup',
    VIEW_OBJECT_GROUP: 'viewObjectGroup',
    VIEW_OBJECT: 'viewObject',
    GEN_IO: 'genio',
    LOGBOOK_VIA_IO2: 'logbookviaio2',
    OPTIONAL_ONETIME_FEE: 'optional_onetime_fee',
    ORDER_DOCUMENT_SUPPORT: 'orderDocumentSupport',
    ORDER_PLANNER_SUPPORT: 'orderPlannerFeatureSupport',
    PERFORM_OBJECT_FUNCTIONS: 'performObjectFunctions',
    DEVICE_HEARTBEAT_SUPPORT: 'deviceHeartbeatSupport',
    PERFORM_FUNCTIONS: 'performFunctions',
    PLUGINS: 'plugins',
    PURCHASE_FUEL_CARD: 'fuelcardPurchases',
    QUERY_POSITION: 'queryPos',
    REMAINING_DRIVING_TIME: 'remaining_drvng_time',
    REMOTE_TACHO: 'remote_tacho',
    SERVICE_FEE: 'service_fee',
    TPMS_NOTIFICATION_VIAIO_2: 'tpmsnotificationviaio2',
    TRACK_HD: 'hd_tracking',
    UPGRADE_PND: 'pnd_upgrade',
    VIEW_LOGBOOK: 'viewLogbook',
    VIEW_POSITION: 'viewPosition',

    // Object group rights
    ...ObjectGroupControlFlags,

    // Object detail rights
    AXLE_WEIGHT_FRONT_WRITABLE: 'axleWeightFrontWritable',
    AXLE_WEIGHT_REAR_WRITABLE: 'axleWeightRearWritable',
    ENGINE_TOTAL_HOURS_WRITEABLE: 'engineTotalHoursWritable',
    ENGINE_TOTAL_HOURS: 'engineTotalHours',
    FUEL_TYPE_WRITABLE: 'fuelTypeWritable',
    GROSS_WEIGHT_WRITABLE: 'grossWeightWritable',
    HEIGHT_WRITABLE: 'heightWritable',
    LENGTH_WRITABLE: 'lengthWritable',
    MAX_SPEED_WRITABLE: 'maxSpeedWritable',
    TANK_SIZE_WRITABLE: 'tankSizeWritable',
    WIDTH_WRITABLE: 'widthWritable',

    // Driver rights
    ASSIGN_OBJECT_MANUALLY: 'assignObjectManually',
    UNASSIGN_OBJECT_MANUALLY: 'unassignObjectManually',

    // License driver rights
    DRIVING_LICENSE: 'drivingLicense',
    DRIVING_LICENSE_CHECK: 'drivingLicenseCheck',

    DASHBOARD: 'dashboard',

    // Report rights
    REPORTS: 'reports',
    VIEW_REPORT: 'viewReport',
    EDIT_SCHEDULED_REPORTS: 'editScheduledReports',
    EDIT_NON_OWNED_SCHEDULED_REPORTS: 'editNonOwnedScheduledReports',
    DELETE_ARCHIVED_REPORTS: 'deleteArchivedReports',
    REPORTS_VIEW_ARCHIVE: 'reportsViewArchive',
    REPORTS_VIEW_MANAGER: 'reportsViewManager',

    // Logbook rights
    LOGBOOK: 'logbook',
    EDIT_LOG_BOOK: 'editLogbook',

    // Order rights
    ORDERS: 'orders',
    VIEW_ORDER: 'viewOrder',
    EDIT_ORDER: 'editOrder',
    ORDER_SCHEDULE_OPTIMIZATION: 'order_schedule_optimization',

    /**
     * Map rights
     *
     * Sidenote #1: Yes, there is a typo in 'satelite'. But we left all
     * corresponding variables with that typo (e.g. MAP_TYPE_GOOGLE_SATELITE)
     * to make searching for this specific controlflag easier, because
     * we can't rename the controlflag in the backend without further pain.
     */
    CURRENT_POSITION: 'currentPosition',
    DETAIL_MAP: 'detailMap',
    DRIVING_EVENTS: 'drivingEvents',
    MAP: 'map',
    MAP_CLICK_POINT: 'mapClickPoint',
    MAP_FIND_LOCATION: 'mapFindLocation',
    MAP_SEARCH: 'mapSmartSearch',
    MAP_TYPE_GOOGLE_ROAD: 'mapTypeGoogleRoad',
    MAP_TYPE_GOOGLE_SATELITE: 'mapTypeGoogleSatelite', // @see #1
    MAP_TYPE_TOMTOM: 'mapTypeTomTom',
    MAP_TYPE_TOMTOM_TRAFFIC: 'mapTypeTomTomTraffic',
    MAP_VEHICLE_GROUPED: 'mapVehicleGrouped',
    STREET_VIEW: 'streetViewPreview',
    TRACK_FUEL_DEPENDENT_COLORING: 'trackFuelDependentColoring',
    TRACK_SPEED_DEPENDENT_COLORING: 'trackSpeedDependentColoring',
    TRACK: 'track',

    // Vehicle rights
    ASSETS: 'assets',
    DRIVER_ASSIGNMENT: 'driverAssignment',
    EDIT_VEHICLE_IMAGES: 'editVehicleImages',
    EXTERNAL_OBJECT_DATA: 'externalObjectData',
    FUEL_DATA: 'fuelData',
    IMMOBILIZER: 'immobilizer',
    IMMOBILIZER_OUTPUT: 'immobilizerOutput',
    SATELLITE: 'satellite',
    SEND_MESSAGE: 'sendMessage',
    SEND_ORDER: 'sendOrder',
    SEND_ROUTE: 'sendRoute',
    SEND_TEXT: 'sendtext',
    SMART_TACHOGRAPH_SUPPORT: 'smartTachographSupport',
    SPEEDING_EVENTS: 'speedingEvents',
    STANDSTILLS: 'standstills',
    SWAP_VEHICLE: 'swapVehicle',
    TRACK_ONLY: 'trackOnly',
    TRIPS: 'trips',
    VEHICLES: 'vehicles',
    VEHICLES_COMPACT_VIEW: 'vehiclesCompactView',
    VEHICLES_POSITION_STATUS: 'vehiclesPositionStatus',
    VIEW_TRACK: 'viewTrack',
    VORTEX_DELETE: 'vortexDeletion',

    // Settings rights
    EDIT_USER_SETTINGS: 'editUserSettings',
    EDIT_ACCOUNT_SETTINGS: 'editAccountSettings',
    NOTIFICATION_FORWARDING: 'notificationForwarding',
    EXTERNAL_IDENTITY_MANAGEMENT: 'externalIdentityManagement',
    DEVICE_HEARTBEAT_CONFIGURATION_ENABLED: 'deviceHeartbeatConfigurationEnabled',
    SMART_NOTIFICATION_2: 'smartNotification2',

    // Invoice rights
    INVOICES: 'invoices',
    VIEW_INVOICE: 'viewInvoice',

    // Administration rights
    VIEW_SUBSCRIPTION_ADMINISTRATION: 'viewSubscriptionAdministration',

    // Maintenance rights
    MAINTENANCE: 'maintenance',
    VEHICLE_MAINTENANCE_SCHEDULE_EDITABLE: 'vehicleMaintenanceScheduleEditable',
    VEHICLE_MAINTENANCE_SCHEDULE_DELETABLE: 'vehicleMaintenanceScheduleDeletable',
    VEHICLE_MAINTENANCE_TASK_MARKABLE_AS_READ: 'vehicleMaintenanceTaskMarkableAsRead',
    VEHICLE_MAINTENANCE_TASK_MARKABLE_AS_COMPLETE: 'vehicleMaintenanceTaskMarkableAsComplete',
    VEHICLE_MAINTENANCE_TASK_DELETABLE: 'vehicleMaintenanceTaskDeletable',

    // Vehicle diagnostic
    VEHICLE_DIAGNOSTIC_DTC_VIEW_ENABLED: 'vehicleDiagnosticDTCViewEnabled',
    VEHICLE_DIAGNOSTICS_HCV: 'vehicle_diagnostics_hcv',
    VEHICLE_DIAGNOSTICS_LCV: 'vehicle_diagnostics_lcv',

    // Maintenance settings rights
    EDIT_VEHICLE_BATTERY_MONITORING_SETTINGS: 'editVehicleBatteryMonitoringSettings',

    // Document rights
    EDIT_ACCOUNT_DOCUMENT: 'editAccountDocument',

    // report incinent rights
    REPORT_UNIT_INCIDENT: 'reportUnitIncident',

    // Jackfish data
    JACKFISH_DATA_XS: 'jackfish_data_xs',
    JACKFISH_DATA_S: 'jackfish_data_s',
    JACKFISH_DATA_M: 'jackfish_data_m',
    JACKFISH_DATA_L: 'jackfish_data_l',
    JACKFISH_DATA_XL: 'jackfish_data_xl',

    // Features dependent control flags
    ASSIGN_OBJECT_TO_DRIVER_GROUP: 'assignDriverGroup',

    // TPMS control flags
    TPMS: 'tpms', // customer booking WF_TPMS
    VIEW_TPMS_DATA: 'viewTpmsData',
    VIEW_TIRE_DATA: 'viewTireData',
    EDIT_TIRE_DATA: 'editTireData',
    TIRE_SERVICE_APP_ACCESS: 'tireServiceAppAccess',
    TIRE_WEAR_DATA: 'tireWearData',
    TIRE_MOUNTED_SENSOR_DATA: 'tireMountedSensorData',
    TIRE_HEALTH_DATA: 'tireHealthData',
    WF_TPMS_CAN: 'wf_tpms_can',
    // TODO: Move this control flag to the right section.
    ACCESS_EXTERNAL_TACHO_GRAPH: 'accessExternalTachoGraph',

    // Vehicle checklist
    VEHICLE_CHECKLISTS: 'vehicleChecklists',
    EDIT_VEHICLE_CHECKLISTS: 'editVehicleChecklists',

    // TachoShare
    TACHOSHARE_UI: 'tachoshareUI',
    EXTERNAL_COORDINA_TACHOGRAPH: 'externalCoordinaTachograph',
    TACHOSHARE_TIS_WEB_CONNECT: 'tachoShareTISWebConnect',
    TACHOSHARE: 'tachoShare',

    // TouCAN control flags
    TOU_USER: 'touUser',
    TOU_ADMIN: 'touAdmin',

    // Hours of service North America control flag
    ACCESS_EXTERNAL_HOS_NAM: 'accessExternalHosNAm',

    // Satellite data
    SATELLITE_DATA_S: 'satellite_data_s',
    SATELLITE_DATA_M: 'satellite_data_m',
    SATELLITE_DATA_L: 'satellite_data_l',
    SATELLITE_DATA_XL: 'satellite_data_xl',
    SATELLITE_DATA_XS: 'satellite_data_xs',

    // Satellite consumption control flag
    SATELLITE_DATA_CONSUMPTION: 'satelliteDataConsumption',

    // Cold Chain
    COLD_CHAIN: 'coldChain',
    COLD_CHAIN_REPORTING: 'coldChainReporting',
    COLD_CHAIN_INVERTABLE_IO: 'coldChainInvertableIo',

    // Eruc access control flag
    ACCESS_ERUC: 'accessEruc',

    // Ebpms data access control flag
    EBPMS_DATA: 'ebpmsData',

    // Ebs data access control flag
    EBS_DATA: 'ebsData',

    // Vin Decoding control flag
    VIN_DECODING: 'vinDecoding',

    // EV Transition Tool access control flag
    ELECTRIC_VEHICLE_TRANSITION: 'electricVehicleTransition',

    // Smart charging advert access control flag
    VIEW_SMART_CHARGING_ADVERT: 'viewSmartChargingAdvert',

    // Smart charging UI features control flags
    EV_CHARGER_MONITORING_AC: 'evChargerMonitoringAC',
    EV_CHARGER_MONITORING_DC: 'evChargerMonitoringDC',
    EV_CHARGER_MONITORING_OPTIMIZATION_AC: 'evChargerMonitoringOptimizationAC',
    EV_CHARGER_MONITORING_OPTIMIZATION_DC: 'evChargerMonitoringOptimizationDC',

    // Secure truck parking access control flags
    EDIT_SECURE_TRUCK_PARKING_BOOKING: 'editSecureTruckParkingBooking',
    VIEW_SECURE_TRUCK_PARKING_BOOKING: 'viewSecureTruckParkingBooking',

    // electric vehicle features access control flag
    BATTERY_INSIGHT: 'wf_battery_insight',
    EV_INSIGHTS_AND_RELIABILITY: 'wf_ev_insights_and_reliability',
    EV_NOTIFICATIONS: 'wf_ev_notifications'
}
Object.freeze(ControlFlags)
